import { useState } from 'react';
import PageHeader from '@/components/layout/PageHeader';
import CellarGrid from '@/components/cellar/CellarGrid';
import CellarList from '@/components/cellar/CellarList';
import { useCellar } from '@/hooks/useCellar';

type ViewMode = 'grid' | 'list';
type Filter = 'all' | 'RED' | 'WHITE' | 'ROSE' | 'SPARKLING';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'RED', label: 'Reds' },
  { key: 'WHITE', label: 'Whites' },
  { key: 'ROSE', label: 'Rosé' },
  { key: 'SPARKLING', label: 'Sparkling' },
];

export default function Cellar() {
  const { items, loading } = useCellar();
  const [view, setView] = useState<ViewMode>('grid');
  const [filter, setFilter] = useState<Filter>('all');

  const drinkSoon = items.filter(
    (i) => i.drinkBy && new Date(i.drinkBy) <= new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
  );

  const filtered = filter === 'all'
    ? items
    : items.filter((i) => i.wine.style === filter);

  const totalBottles = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <div className="page page-cellar">
      <PageHeader
        title="My Cellar"
        right={
          <div className="view-toggle">
            <button
              className={`view-btn ${view === 'grid' ? 'active' : ''}`}
              onClick={() => setView('grid')}
              aria-label="Grid view"
            >
              <GridIcon />
            </button>
            <button
              className={`view-btn ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')}
              aria-label="List view"
            >
              <ListIcon />
            </button>
          </div>
        }
      />

      {!loading && (
        <p className="cellar-count">{totalBottles} bottle{totalBottles !== 1 ? 's' : ''}</p>
      )}

      {drinkSoon.length > 0 && (
        <section className="cellar-section">
          <h2 className="section-title">Drink soon</h2>
          {view === 'grid' ? <CellarGrid items={drinkSoon} /> : <CellarList items={drinkSoon} />}
        </section>
      )}

      <div className="filter-pills">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`filter-pill ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            {f.key !== 'all' && (
              <span className="filter-count">
                {items.filter((i) => i.wine.style === f.key).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="loading-text">Loading cellar…</p>
      ) : view === 'grid' ? (
        <CellarGrid items={filtered} emptyMessage={`No ${filter === 'all' ? '' : filter.toLowerCase() + ' '}wines in cellar.`} />
      ) : (
        <CellarList items={filtered} />
      )}
    </div>
  );
}

function GridIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" width={18} height={18}>
      <rect x="2" y="2" width="7" height="7" rx="1" />
      <rect x="11" y="2" width="7" height="7" rx="1" />
      <rect x="2" y="11" width="7" height="7" rx="1" />
      <rect x="11" y="11" width="7" height="7" rx="1" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} width={18} height={18}>
      <path d="M4 5h12M4 10h12M4 15h12" strokeLinecap="round" />
    </svg>
  );
}
